import type { SpaceOverlayIntent } from '@toolproof-core/visualization/runtime/_contract/overlay';

import { isBooleanAlgebraOperation } from '@/adapters/primitive/_pure/derivation/boolean/algebra';
import {
    SECTION_BOOLEAN_OPERAND_TWO,
    updatePrimitiveOperationInputDraft,
} from '@/adapters/primitive/_pure/interaction/operationInput';
import {
    ACTION_NEXT,
    ACTION_PLAY_PAUSE,
    ACTION_PREV,
    ACTION_RESET,
    SECTION_OPERATION,
    buildPrimitiveOverlayModel,
    isPrimitiveOperation,
} from '@/adapters/primitive/_pure/interaction/overlay';
import {
    completePrimitiveTransition,
    createInitialPrimitiveRuntimeState,
    requestNextPrimitiveStep,
    retreatPrimitiveStep,
    togglePrimitivePlayback,
} from '@/adapters/primitive/_pure/model/runtimeState';
import { createEpisodeController, createEpisodeEffectOwner } from '@/adapters/primitive/episodePlayback';
import { createPrimitiveStepResolver } from '@/adapters/primitive/traceResolver';
import type { SpaceAdapterRuntimeController } from '@/_contract/types';
import type {
    Config,
    CosmosDataModel,
    RuntimeState,
} from '@/adapters/primitive/_contract/config';

const stepResolvers = new WeakMap<RuntimeState['inputDraft'], ReturnType<typeof createPrimitiveStepResolver>>();

function getSteps(config: Config, runtimeState: RuntimeState) {
    let resolver = stepResolvers.get(runtimeState.inputDraft);
    if (!resolver) {
        resolver = createPrimitiveStepResolver();
        stepResolvers.set(runtimeState.inputDraft, resolver);
    }
    return resolver(config, runtimeState);
}

function getMaxStepIndex(config: Config, runtimeState: RuntimeState): number {
    return Math.max(0, getSteps(config, runtimeState).length - 1);
}

const baseRuntimeController = {
    createInitialRuntimeState: () => createInitialPrimitiveRuntimeState(),
    reducePlaybackSuspension: ({ runtimeState, suspended, resumeState }: {
        runtimeState: RuntimeState; suspended: boolean; resumeState: RuntimeState;
    }): RuntimeState => ({
        ...runtimeState,
        isPlaying: suspended ? false : resumeState.isPlaying,
    }),
    buildOverlayModel: (args) => buildPrimitiveOverlayModel({ ...args, steps: getSteps(args.config, args.runtimeState) }),
    reduceOverlayIntent: ({ config, runtimeState, intent }: {
        config: Config;
        runtimeState: RuntimeState;
        intent: SpaceOverlayIntent;
        cosmosData: CosmosDataModel;
    }): RuntimeState => {
        const unfinished = runtimeState.pendingStepIndex !== null || runtimeState.isPlaying;
        if (unfinished && intent.overlayIntentKind !== 'action-triggered') return runtimeState;
        if (intent.overlayIntentKind === 'select-changed') {
            if (intent.sectionId !== SECTION_OPERATION || !isPrimitiveOperation(intent.value)) {
                const inputDraft = updatePrimitiveOperationInputDraft({
                    draft: runtimeState.inputDraft,
                    sectionId: intent.sectionId,
                    value: intent.value,
                });
                if (inputDraft === null || JSON.stringify(inputDraft) === JSON.stringify(runtimeState.inputDraft)) return runtimeState;
                runtimeState = { ...runtimeState, selectedTrace: undefined };
                if (isBooleanAlgebraOperation(inputDraft.operation)) {
                    const inputRole = intent.sectionId === SECTION_BOOLEAN_OPERAND_TWO
                        ? 'boolean-input-two' as const : 'boolean-input-one' as const;
                    if (runtimeState.booleanAssignment !== undefined) {
                        return {
                            ...runtimeState, inputDraft, inputEditing: true,
                            booleanAssignmentQueue: [
                                ...(runtimeState.booleanAssignmentQueue ?? []),
                                { nextDraft: inputDraft, inputRole },
                            ],
                            committedStepIndex: 0, pendingStepIndex: null, playbackAdvanceMode: 'manual', isPlaying: false,
                        };
                    }
                    return {
                        ...runtimeState, inputDraft, inputEditing: true,
                        booleanAssignment: { previousDraft: runtimeState.inputDraft, nextDraft: inputDraft, inputRole },
                        committedStepIndex: 0, pendingStepIndex: null, playbackAdvanceMode: 'manual', isPlaying: false,
                    };
                }
                return { ...runtimeState, inputDraft, inputEditing: true, committedStepIndex: 0, pendingStepIndex: null, playbackAdvanceMode: 'manual', isPlaying: false };
            }
            if (intent.value === runtimeState.operation) return runtimeState;
            return createInitialPrimitiveRuntimeState(intent.value);
        }

        if (intent.overlayIntentKind === 'text-changed') {
            const inputDraft = updatePrimitiveOperationInputDraft({
                draft: runtimeState.inputDraft,
                sectionId: intent.sectionId,
                value: intent.value,
            });
            return inputDraft === null || JSON.stringify(inputDraft) === JSON.stringify(runtimeState.inputDraft)
                ? runtimeState
                : {
                    ...runtimeState, selectedTrace: undefined, inputDraft, inputEditing: true,
                    committedStepIndex: 0, pendingStepIndex: null, playbackAdvanceMode: 'manual', isPlaying: false,
                };
        }

        if (intent.overlayIntentKind !== 'action-triggered') return runtimeState;

        if (intent.actionId === ACTION_RESET) {
            return {
                ...createInitialPrimitiveRuntimeState(runtimeState.operation),
                inputDraft: runtimeState.inputDraft,
                ...(runtimeState.selectedTrace ? { selectedTrace: runtimeState.selectedTrace } : {}),
            };
        }

        if (intent.actionId === ACTION_PREV) {
            if (unfinished || runtimeState.booleanAssignment !== undefined) return runtimeState;
            return retreatPrimitiveStep(runtimeState);
        }

        if (intent.actionId === ACTION_NEXT) {
            if (unfinished) return runtimeState;
            if (runtimeState.booleanAssignment !== undefined) return runtimeState;
            if (getSteps(config, runtimeState).length === 0) return runtimeState;
            return requestNextPrimitiveStep({
                ...runtimeState,
                inputEditing: false,
                playbackAdvanceMode: 'manual', isPlaying: false,
            }, getMaxStepIndex(config, runtimeState));
        }

        if (intent.actionId === ACTION_PLAY_PAUSE) {
            if (runtimeState.booleanAssignment !== undefined) return runtimeState;
            if (getSteps(config, runtimeState).length === 0) return runtimeState;
            return togglePrimitivePlayback({ ...runtimeState, inputEditing: false }, getMaxStepIndex(config, runtimeState));
        }

        return runtimeState;
    },
    reduceDerivationTransitionComplete: ({ config, runtimeState }: {
        config: Config;
        runtimeState: RuntimeState;
        cosmosData: CosmosDataModel;
    }): RuntimeState => {
        if (runtimeState.booleanAssignment !== undefined) {
            const { booleanAssignment, booleanAssignmentQueue, ...completed } = runtimeState;
            const [next, ...remaining] = booleanAssignmentQueue ?? [];
            if (next !== undefined) return {
                ...completed,
                booleanAssignment: {
                    previousDraft: booleanAssignment.nextDraft,
                    nextDraft: next.nextDraft,
                    inputRole: next.inputRole,
                },
                ...(remaining.length === 0 ? {} : { booleanAssignmentQueue: remaining }),
            };
            return completed;
        }
        return completePrimitiveTransition(runtimeState, getMaxStepIndex(config, runtimeState));
    },
} satisfies SpaceAdapterRuntimeController<
    Config,
    RuntimeState,
    CosmosDataModel
>;

export const runtimeController = createEpisodeController(baseRuntimeController);
export const createRuntimeEffectOwner = () => createEpisodeEffectOwner(runtimeController);